// src/pages/subastas/SubastasPage.tsx
// Panel de Subastas en Vivo — Feriagana

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Client, IMessage } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import {
  AuctionLot,
  SubastaConfig,
  getSubastaConfig,
  updateSubastaConfig,
  togglePromoVideo,
  crearMuxStream,
  refreshMuxStatus,
  forzarEstadoMux,
  CreateLotPayload,
  LotBidAdmin,
  LotEventDTO,
  LotParticipant,
  activarLote,
  activarSiguienteLote,
  approveParticipant,
  cerrarLotePorPuja,
  crearLote,
  formatCOP,
  formatDateEs,
  getBidsAdmin,
  getParticipants,
  listarLotes,
  revokeParticipant,
  statusColor,
  statusLabel,
} from "../../api/auctionLotApi";
import { BASE_URL } from "../../api/client";
import { aprobarUsuario, revocarUsuario } from "../../api/userSubastaApi";

// ─── helpers ──────────────────────────────────────────────────────────────────
function parseVideoUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (raw.startsWith("http")) return raw;
  const match = raw.match(/url=([^,}]+)/);
  if (match) return match[1].trim();
  return raw;
}

function getAdminToken(): string {
  return (
    localStorage.getItem("fg_admin_token") ||
    localStorage.getItem("fg_token") ||
    ""
  );
}

// ─── PALETA BADGE ─────────────────────────────────────────────────────────────
function PaletaBadge({
  number,
  size = 56,
  winner = false,
}: {
  number: number | string;
  size?: number;
  winner?: boolean;
}) {
  const pad = String(number).padStart(3, "0");
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: `2.5px solid ${winner ? "#0cbb1d" : "#e0881a"}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#fff",
        flexShrink: 0,
        boxShadow: winner ? "0 0 0 3px rgba(12,187,29,0.15)" : "none",
      }}
    >
      <span
        style={{
          fontWeight: 800,
          fontSize: size > 40 ? 15 : 12,
          color: "#1a3a2a",
        }}
      >
        {pad}
      </span>
    </div>
  );
}

// ─── BID CARD ─────────────────────────────────────────────────────────────────
function BidCard({
  bid,
  isFirst,
  onClick,
  disabled,
}: {
  bid: LotBidAdmin;
  isFirst: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 16px",
        borderRadius: 14,
        backgroundColor: isFirst ? "#006b4c" : "#fff",
        border: isFirst ? "none" : "1px solid #e8eae8",
        cursor: disabled ? "default" : "pointer",
        marginBottom: 10,
        transition: "all 0.15s",
        boxShadow: isFirst
          ? "0 2px 8px rgba(26,110,58,0.18)"
          : "0 1px 3px rgba(0,0,0,0.05)",
      }}
      onMouseEnter={(e) => {
        if (!disabled && !isFirst)
          (e.currentTarget as HTMLDivElement).style.boxShadow =
            "0 2px 8px rgba(0,0,0,0.12)";
      }}
      onMouseLeave={(e) => {
        if (!disabled && !isFirst)
          (e.currentTarget as HTMLDivElement).style.boxShadow =
            "0 1px 3px rgba(0,0,0,0.05)";
      }}
    >
      <PaletaBadge number={bid.palletNumber} size={52} />
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: isFirst ? "rgba(255,255,255,0.8)" : "#888",
            marginBottom: 2,
          }}
        >
          {isFirst ? "Último valor" : "Valor ofrecido"}
        </div>
        <div
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: isFirst ? "#fff" : "#0f3644",
            lineHeight: 1.2,
          }}
        >
          {formatCOP(bid.amount)}
        </div>
        <div
          style={{
            fontSize: 11,
            color: isFirst ? "rgba(255,255,255,0.7)" : "#aaa",
            marginTop: 2,
          }}
        >
          {formatDateEs(bid.createdAt)}
        </div>
      </div>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: isFirst ? "#fff" : "rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M3 8l3.5 3.5L13 4"
            stroke={isFirst ? "#0cbb1d" : "#B3B3B3"}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}

// ─── LOT VIDEO PLAYER ─────────────────────────────────────────────────────────
function LotVideoPlayer({
  lot,
  style,
  controls = false,
}: {
  lot: { videoUrl: string | null; videoSource?: string | null } | null;
  style?: React.CSSProperties;
  controls?: boolean;
}) {
  if (!lot?.videoUrl) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...style,
        }}
      >
        <div style={{ textAlign: "center", color: "rgba(255,255,255,0.5)" }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>▶</div>
          <div style={{ fontSize: 13 }}>Sin video activo</div>
        </div>
      </div>
    );
  }

  const isYT =
    lot.videoSource === "YOUTUBE" || lot.videoUrl.includes("youtube.com/embed");

  if (isYT) {
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          ...style,
        }}
      >
        <iframe
          src={lot.videoUrl}
          title="Video del lote"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      </div>
    );
  }

  return (
    <video
      src={lot.videoUrl}
      style={{ width: "100%", height: "100%", objectFit: "cover", ...style }}
      muted
      autoPlay
      loop
      playsInline
      controls={controls}
    />
  );
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
export default function SubastasPage() {
  const [lots, setLots] = useState<AuctionLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [selectedLotId, setSelectedLotId] = useState<string | null>(null);

  const [bids, setBids] = useState<LotBidAdmin[]>([]);
  const [bidsLoading, setBidsLoading] = useState(false);

  const [participants, setParticipants] = useState<LotParticipant[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);

  const [closingBid, setClosingBid] = useState<LotBidAdmin | null>(null);
  const [showForm, setShowForm] = useState(false);

  // ─── SUBASTA CONFIG ────────────────────────────────────────────────────────
  const [subastaConfig, setSubastaConfig] = useState<SubastaConfig>({
    promoVideoUrl: null,
    promoActivo: false,
  });
  const [creatingMux, setCreatingMux] = useState(false);
  const [muxMsg, setMuxMsg] = useState("");
  const [forcingMuxStatus, setForcingMuxStatus] = useState(false);
  const [togglingPromo, setTogglingPromo] = useState(false);
  const [savingPromoUrl, setSavingPromoUrl] = useState(false);
  const [promoUrlMsg, setPromoUrlMsg] = useState("");
  const [form, setForm] = useState<CreateLotPayload>({ cantAnimales: 1 });
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const wsRef = useRef<Client | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [videoExpanded, setVideoExpanded] = useState(false);

  // ─── LOAD ─────────────────────────────────────────────────────────────────

  const loadLots = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarLotes();
      const visibles = data
        .filter((l) => l.status === "PENDIENTE" || l.status === "ACTIVO")
        .map((l) => ({ ...l, videoUrl: parseVideoUrl(l.videoUrl) }));
      setLots(visibles);
      const active = visibles.find((l) => l.status === "ACTIVO");
      if (active) setSelectedLotId(active.id);
      else if (data.length > 0 && !selectedLotId) setSelectedLotId(data[0].id);
    } catch (e: any) {
      showError(e?.response?.data?.message || "Error cargando lotes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLots();
  }, [loadLots]);

  useEffect(() => {
    getSubastaConfig()
      .then((cfg) => setSubastaConfig(cfg))
      .catch(() => {});
  }, []);

  const loadBids = useCallback(async (lotId: string) => {
    setBidsLoading(true);
    try {
      const data = await getBidsAdmin(lotId);
      setBids(data);
    } catch {
      setBids([]);
    } finally {
      setBidsLoading(false);
    }
  }, []);

  const loadParticipants = useCallback(async (lotId: string) => {
    setParticipantsLoading(true);
    try {
      const data = await getParticipants(lotId);
      setParticipants(data);
    } catch {
      setParticipants([]);
    } finally {
      setParticipantsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedLotId) return;
    loadBids(selectedLotId);
    loadParticipants(selectedLotId);
  }, [selectedLotId, loadBids, loadParticipants]);

  // ─── WEBSOCKET ─────────────────────────────────────────────────────────────

  const handleLotEventRef = useRef<(evt: LotEventDTO) => void>(() => {});
  useEffect(() => {
    handleLotEventRef.current = handleLotEvent;
  });

  useEffect(() => {
    const token = getAdminToken();
    const client = new Client({
      webSocketFactory: () => new SockJS(`${BASE_URL}/ws`) as any,
      connectHeaders: token ? { Authorization: `Bearer ${token}` } : {},
      reconnectDelay: 5000,
      onConnect: () => {
        setWsConnected(true);
        client.subscribe("/topic/lots", (msg: IMessage) => {
          try {
            handleLotEventRef.current(JSON.parse(msg.body));
          } catch {}
        });
      },
      onStompError: () => setWsConnected(false),
      onDisconnect: () => setWsConnected(false),
      onWebSocketClose: () => setWsConnected(false),
      debug: () => {},
    });
    wsRef.current = client;
    client.activate();
    return () => {
      try {
        client.deactivate();
      } catch {}
      wsRef.current = null;
      setWsConnected(false);
    };
  }, []);

  const handleLotEvent = (evt: LotEventDTO) => {
    const evtType = (evt as any).type ?? evt.type;

    if (evtType === "LOT_BID") {
      const bidEvt = evt as any;
      if (selectedLotId === bidEvt.lotId) {
        setBids((prev: LotBidAdmin[]) => {
          const newBid: LotBidAdmin = {
            bidId: bidEvt.bidId ?? "",
            palletNumber: Number(bidEvt.palletNumber ?? 0),
            amount: Number(bidEvt.amount ?? bidEvt.currentBid ?? 0),
            createdAt: bidEvt.createdAt ?? new Date().toISOString(),
            userId: bidEvt.userId ?? null,
            userName: bidEvt.userName ?? "—",
            userEmail: null,
            esUltimaOferta: false,
          };
          const updated = [
            newBid,
            ...prev.map((b) => ({ ...b, esUltimaOferta: false })),
          ];
          if (updated.length > 0) updated[0].esUltimaOferta = true;
          return updated;
        });
      }
      const currentBid = Number(bidEvt.currentBid ?? bidEvt.amount ?? 0);
      const bidCount = Number(bidEvt.bidCount ?? 0);
      if (currentBid > 0) {
        setLots((prev) =>
          prev.map((l) =>
            l.id === bidEvt.lotId ? { ...l, currentBid, bidCount } : l,
          ),
        );
      }
      return;
    }

    setLots((prev) => {
      const exists = prev.find((l) => l.id === evt.lotId);
      const upd: AuctionLot = {
        id: evt.lotId,
        nroLote: evt.nroLote,
        orden: evt.orden,
        videoUrl: parseVideoUrl(evt.videoUrl),
        cantAnimales: evt.cantAnimales,
        tipoGanado: evt.tipoGanado,
        pesoProm: evt.pesoProm,
        pesoTotal: evt.pesoTotal,
        ubicacion: evt.ubicacion,
        precioMinimo: evt.precioMinimo,
        valorKg: evt.valorKg,
        valorPorAnimal: evt.valorPorAnimal,
        observaciones: null,
        status: evt.status,
        paletaGanadora: evt.paletaGanadora,
        usuarioGanadorId: evt.usuarioGanadorId,
        montoGanador: evt.montoGanador,
        publishedId: null,
        createdAt: null,
        updatedAt: null,
      };
      if (!exists) return [...prev, upd].sort((a, b) => a.orden - b.orden);
      return prev
        .map((l) => (l.id === evt.lotId ? { ...l, ...upd } : l))
        .sort((a, b) => a.orden - b.orden);
    });

    if (evtType === "LOT_ACTIVATED") {
      setSelectedLotId(evt.lotId);
      loadBids(evt.lotId);
    }
    if (evtType === "LOT_CLOSED") {
      setLots((prev) => prev.filter((l) => l.id !== evt.lotId));
      loadLots();
    }
  };

  // ─── MENSAJES ──────────────────────────────────────────────────────────────

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 4000);
  };
  const showError = (msg: string) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(""), 5000);
  };

  // ─── HANDLERS CONFIG ──────────────────────────────────────────────────────

  const handleCrearMuxStream = async () => {
    setCreatingMux(true);
    setMuxMsg("");
    try {
      const data = await crearMuxStream();
      setSubastaConfig((prev) => ({
        ...prev,
        streamType: "MUX",
        muxRtmpUrl: data.muxRtmpUrl,
        muxStreamKey: data.muxStreamKey,
        muxPlaybackUrl: data.muxPlaybackUrl,
        muxStreamId: data.muxStreamId,
        muxStatus: data.status,
      }));
      setMuxMsg("✅ Stream Mux creado. Configura OBS con los datos de abajo.");
    } catch {
      setMuxMsg(
        "❌ Error creando stream Mux. Verifica las credenciales en application.yml",
      );
    } finally {
      setCreatingMux(false);
    }
  };

  const handleForzarActivo = async () => {
    setForcingMuxStatus(true);
    setMuxMsg("");
    try {
      await forzarEstadoMux("active");
      setSubastaConfig((prev) => ({ ...prev, muxStatus: "active" }));
      setMuxMsg("✅ Estado forzado a ACTIVE — el video debe aparecer en OBS.");
    } catch {
      setMuxMsg("❌ Error forzando estado.");
    } finally {
      setForcingMuxStatus(false);
    }
  };

  const handleForzarIdle = async () => {
    setForcingMuxStatus(true);
    try {
      await forzarEstadoMux("idle");
      setSubastaConfig((prev) => ({ ...prev, muxStatus: "idle" }));
      setMuxMsg("⏹ Estado forzado a IDLE.");
    } catch {
    } finally {
      setForcingMuxStatus(false);
    }
  };

  const handleTogglePromo = async () => {
    setTogglingPromo(true);
    try {
      const nuevoEstado = !subastaConfig.promoActivo;
      await togglePromoVideo(nuevoEstado);
      setSubastaConfig((prev) => ({ ...prev, promoActivo: nuevoEstado }));
    } catch {
      showError("Error cambiando estado del video promo");
    } finally {
      setTogglingPromo(false);
    }
  };

  const handleSubmitLote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.cantAnimales || form.cantAnimales < 1)
      return showError("La cantidad de animales es obligatoria");
    setActionLoading(true);
    try {
      const created = await crearLote({
        ...form,
        video: videoFile,
        onUploadProgress: (pct) => setUploadProgress(pct),
      });
      showSuccess(`✓ Lote ${created.nroLote} registrado`);
      setShowForm(false);
      setForm({ cantAnimales: 1 });
      setVideoFile(null);
      if (videoRef.current) videoRef.current.value = "";
    } catch (e: any) {
      showError(e?.response?.data?.message || "Error registrando lote");
    } finally {
      setActionLoading(false);
      setUploadProgress(null);
    }
  };

  const handleActivar = async (lot: AuctionLot) => {
    if (
      !window.confirm(
        `¿Activar Lote ${lot.nroLote}? El video se cargará en la app móvil.`,
      )
    )
      return;
    setActionLoading(true);
    try {
      await activarLote(lot.id);
      showSuccess(`✓ Lote ${lot.nroLote} activado`);
      setSelectedLotId(lot.id);
    } catch (e: any) {
      showError(e?.response?.data?.message || "Error activando lote");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSiguiente = async () => {
    if (!window.confirm("¿Activar el siguiente lote?")) return;
    setActionLoading(true);
    try {
      const next = await activarSiguienteLote();
      showSuccess(`✓ Siguiente lote activado: ${next.nroLote}`);
      setSelectedLotId(next.id);
    } catch (e: any) {
      showError(e?.response?.data?.message || "No hay más lotes pendientes");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBidClick = (bid: LotBidAdmin) => {
    const activeLot = lots.find(
      (l) => l.id === selectedLotId && l.status === "ACTIVO",
    );
    if (!activeLot) return;
    setClosingBid(bid);
  };

  const handleConfirmClose = async () => {
    if (!closingBid || !selectedLotId) return;
    setActionLoading(true);
    try {
      await cerrarLotePorPuja(selectedLotId, closingBid.bidId);
      showSuccess(
        `✓ Subasta cerrada. Ganador paleta #${String(closingBid.palletNumber).padStart(3, "0")}`,
      );
      setClosingBid(null);
      loadLots();
    } catch (e: any) {
      showError(e?.response?.data?.message || "Error cerrando la subasta");
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async (userId: string) => {
    if (!selectedLotId) return;
    try {
      await aprobarUsuario(userId);
      await approveParticipant(selectedLotId, userId);
      showSuccess("✓ Participante aprobado");
      loadParticipants(selectedLotId);
    } catch (e: any) {
      showError(e?.response?.data?.message || "Error aprobando participante");
    }
  };

  const handleRevoke = async (userId: string) => {
    if (!selectedLotId) return;
    try {
      await revocarUsuario(userId);
      await revokeParticipant(selectedLotId, userId);
      showSuccess("✓ Aprobación revocada — el usuario ya no puede pujar");
      loadParticipants(selectedLotId);
    } catch (e: any) {
      showError(e?.response?.data?.message || "Error revocando participante");
    }
  };

  // ─── DATOS DERIVADOS ───────────────────────────────────────────────────────

  const selectedLot = lots.find((l) => l.id === selectedLotId) ?? null;
  const activeLot = lots.find((l) => l.status === "ACTIVO") ?? null;
  const isSelectedActive = selectedLot?.status === "ACTIVO";

  // ─── RENDER ────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f4f5f4",
        padding: "24px",
      }}
    >
      {/* ── TOP BAR ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontWeight: 800,
              color: "#1a3a2a",
              fontSize: 22,
            }}
          >
            🔨 Subastas en Vivo
          </h2>
          <p style={{ margin: "3px 0 0", color: "#666", fontSize: 13 }}>
            Gestión de lotes de ganado
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span
            style={{
              padding: "4px 12px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 700,
              backgroundColor: wsConnected ? "#d4edda" : "#f8d7da",
              color: wsConnected ? "#006b4c" : "#D60D4C",
            }}
          >
            {wsConnected ? "● En vivo (WS)" : "○ Sin conexión WS"}
          </span>
          <button
            onClick={handleSiguiente}
            disabled={actionLoading}
            style={btnStyle("#006b4c")}
          >
            ▶ Siguiente Lote
          </button>
          <button onClick={() => setShowForm(true)} style={btnStyle("#0cbb1d")}>
            + Registrar Lote
          </button>
        </div>
      </div>

      {/* ── PANEL CONFIG: Mux + Promo ── */}
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: 14,
          padding: "14px 18px",
          marginBottom: 16,
          border: "1px solid #e8eae8",
          display: "flex",
          gap: 16,
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        {/* ── Panel Mux ── */}
        <div
          style={{
            flex: "1 1 360px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
            borderLeft: "1px solid #e8eae8",
            paddingLeft: 16,
          }}
        >
          <label style={{ fontWeight: 700, fontSize: 13, color: "#1a3a2a" }}>
            📡 Mux — Stream en Vivo
          </label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={handleCrearMuxStream}
              disabled={creatingMux}
              style={{
                ...btnStyle("#006b4c"),
                opacity: creatingMux ? 0.6 : 1,
              }}
            >
              {creatingMux
                ? "Creando..."
                : subastaConfig.muxStreamId
                  ? "🔄 Recrear Stream"
                  : "➕ Crear Stream Mux"}
            </button>

            <button
              onClick={handleForzarActivo}
              disabled={forcingMuxStatus}
              title="Fuerza el estado a EN VIVO cuando el webhook no llega automáticamente"
              style={{
                ...btnStyle("#dc3545"),
                opacity: forcingMuxStatus ? 0.6 : 1,
                fontSize: 12,
              }}
            >
              {forcingMuxStatus ? "..." : "▶ Forzar EN VIVO"}
            </button>
            <button
              onClick={handleForzarIdle}
              disabled={forcingMuxStatus}
              title="Fuerza el estado a IDLE cuando OBS se desconecta"
              style={{
                ...btnStyle("#888"),
                opacity: forcingMuxStatus ? 0.6 : 1,
                fontSize: 12,
              }}
            >
              ⏹ Forzar IDLE
            </button>
          </div>

          {muxMsg && (
            <small
              style={{
                color: muxMsg.startsWith("✅") ? "#0cbb1d" : "#dc3545",
                fontWeight: 700,
              }}
            >
              {muxMsg}
            </small>
          )}

          {subastaConfig.muxStreamId && (
            <div
              style={{
                backgroundColor: "#f0fff4",
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 12,
                border: "1px solid #0cbb1d40",
              }}
            >
              <div
                style={{ fontWeight: 700, marginBottom: 6, color: "#006b4c" }}
              >
                📋 Configuración para OBS:
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Servidor:</strong>{" "}
                <code
                  style={{
                    backgroundColor: "#e8f5e9",
                    padding: "1px 6px",
                    borderRadius: 4,
                  }}
                >
                  {subastaConfig.muxRtmpUrl ??
                    "rtmps://global-live.mux.com:443/app/"}
                </code>
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Clave:</strong>{" "}
                <code
                  style={{
                    backgroundColor: "#e8f5e9",
                    padding: "1px 6px",
                    borderRadius: 4,
                    wordBreak: "break-all",
                  }}
                >
                  {subastaConfig.muxStreamKey ?? "—"}
                </code>
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Estado:</strong>{" "}
                <span
                  style={{
                    color:
                      subastaConfig.muxStatus === "active" ? "#0cbb1d" : "#888",
                    fontWeight: 700,
                  }}
                >
                  {subastaConfig.muxStatus === "active"
                    ? "● EN VIVO"
                    : "○ " + (subastaConfig.muxStatus ?? "idle")}
                </span>
              </div>
              <div style={{ color: "#888", marginTop: 4 }}>
                Room:{" "}
                <strong>
                  {subastaConfig.muxStreamId ?? "feriagana-subasta"}
                </strong>
              </div>
            </div>
          )}

          <small style={{ color: "#888", fontSize: 11 }}>
            Mux usa HLS nativo — ~1-3s latencia. OBS: Servidor + Clave de
            retransmisión y listo.
          </small>
        </div>

        {/* ── Panel Video Promo ── */}
        <div
          style={{
            flex: "1 1 260px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
            borderLeft: "1px solid #e8eae8",
            paddingLeft: 16,
          }}
        >
          <label style={{ fontWeight: 700, fontSize: 13, color: "#1a3a2a" }}>
            🎬 Video Promo
          </label>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="file"
              accept="video/mp4,video/mov,video/*"
              disabled={savingPromoUrl}
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setSavingPromoUrl(true);
                setPromoUrlMsg("");
                const fd = new FormData();
                fd.append("video", f);
                try {
                  const r = await fetch(
                    `${BASE_URL}/admin/subasta-config/promo-upload`,
                    {
                      method: "POST",
                      headers: { Authorization: `Bearer ${getAdminToken()}` },
                      body: fd,
                    },
                  );
                  const d = await r.json();
                  if (!r.ok) throw new Error(d?.message || "Error");
                  setSubastaConfig((prev) => ({
                    ...prev,
                    promoVideoUrl: d.promoVideoUrl,
                  }));
                  setPromoUrlMsg("✅ Listo");
                  setTimeout(() => setPromoUrlMsg(""), 3000);
                } catch {
                  setPromoUrlMsg("❌ Error");
                } finally {
                  setSavingPromoUrl(false);
                }
              }}
              style={{ fontSize: 11, flex: 1, cursor: "pointer" }}
            />
            {savingPromoUrl && (
              <small style={{ color: "#888", whiteSpace: "nowrap" }}>
                Subiendo...
              </small>
            )}
          </div>

          {promoUrlMsg && (
            <small
              style={{
                color: promoUrlMsg.startsWith("✅") ? "#0cbb1d" : "#dc3545",
                fontWeight: 700,
              }}
            >
              {promoUrlMsg}
            </small>
          )}

          {subastaConfig.promoVideoUrl && (
            <small style={{ color: "#0cbb1d", fontSize: 10 }}>
              ✓ {subastaConfig.promoVideoUrl.split("/").pop()?.slice(0, 40)}
            </small>
          )}

          <button
            onClick={handleTogglePromo}
            disabled={togglingPromo || !subastaConfig.promoVideoUrl}
            style={{
              ...btnStyle(subastaConfig.promoActivo ? "#dc3545" : "#0cbb1d"),
              opacity: togglingPromo || !subastaConfig.promoVideoUrl ? 0.5 : 1,
              fontSize: 12,
              padding: "6px 14px",
              alignSelf: "flex-start",
            }}
          >
            {togglingPromo
              ? "..."
              : subastaConfig.promoActivo
                ? "⏹ Desactivar"
                : "▶ Activar Promo"}
          </button>
          <small
            style={{
              color: subastaConfig.promoActivo ? "#dc3545" : "#888",
              fontSize: 10,
              fontWeight: subastaConfig.promoActivo ? 700 : 400,
            }}
          >
            {subastaConfig.promoActivo
              ? "● App mostrando promo"
              : "○ App muestra lote activo"}
          </small>
        </div>
      </div>

      {successMsg && <div style={alertStyle("success")}>{successMsg}</div>}
      {errorMsg && <div style={alertStyle("error")}>{errorMsg}</div>}

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>
          Cargando lotes...
        </div>
      ) : lots.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>
          No hay lotes registrados.
          <br />
          <button
            onClick={() => setShowForm(true)}
            style={{ ...btnStyle("#0cbb1d"), marginTop: 14 }}
          >
            + Registrar primer lote
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
          {/* ── COLUMNA IZQUIERDA ── */}
          <div style={{ flex: "0 0 55%", maxWidth: "55%" }}>
            <div
              onClick={() => setVideoExpanded(true)}
              style={{
                borderRadius: 18,
                overflow: "hidden",
                marginBottom: 16,
                position: "relative",
                backgroundColor: "#0f3644",
                height: 450,
                cursor: "pointer",
              }}
            >
              <LotVideoPlayer lot={activeLot} />
              {activeLot?.videoUrl && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      backgroundColor: "rgba(255,255,255,0.7)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="#1a3a2a"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              )}
            </div>

            {/* LOTES TABS */}
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 20,
                  color: "#1a3a2a",
                  marginBottom: 10,
                }}
              >
                Lotes
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {lots.map((lot) => (
                  <button
                    key={lot.id}
                    onClick={() => setSelectedLotId(lot.id)}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 10,
                      border: "none",
                      backgroundColor:
                        lot.id === selectedLotId
                          ? "#006b4c"
                          : "rgba(26,110,58,0.12)",
                      color: lot.id === selectedLotId ? "#fff" : "#1a6e3a",
                      fontWeight: 800,
                      fontSize: 15,
                      cursor: "pointer",
                      outline:
                        lot.status === "ACTIVO" ? "2px solid #0cbb1d" : "none",
                      position: "relative",
                    }}
                  >
                    {lot.nroLote}
                    {lot.status === "ACTIVO" && (
                      <span
                        style={{
                          position: "absolute",
                          top: -4,
                          right: -4,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: "#0cbb1d",
                          border: "2px solid #fff",
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* TARJETA OSCURA DEL LOTE SELECCIONADO */}
            {selectedLot && (
              <div
                style={{
                  backgroundColor: "#0f3644",
                  borderRadius: 18,
                  padding: 20,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: 10,
                  }}
                >
                  <span
                    style={{
                      backgroundColor: "#f98115",
                      color: "#fff",
                      borderRadius: 20,
                      padding: "4px 16px",
                      fontWeight: 700,
                      fontSize: 16,
                    }}
                  >
                    🔨 Subasta
                  </span>
                </div>

                <div style={{ textAlign: "center", marginBottom: 14 }}>
                  <span
                    style={{
                      color: "rgba(255,255,255,0.6)",
                      fontWeight: 400,
                      fontSize: 20,
                    }}
                  >
                    Lote:{" "}
                  </span>
                  <span
                    style={{ color: "#fff", fontWeight: 900, fontSize: 27 }}
                  >
                    {selectedLot.nroLote}
                  </span>
                </div>

                <div
                  style={{ display: "flex", flexDirection: "column", gap: 5 }}
                >
                  {[
                    ["Cant Animales:", selectedLot.cantAnimales],
                    ["Tipo de ganado:", selectedLot.tipoGanado || "—"],
                    [
                      "Peso Prom:",
                      selectedLot.pesoProm ? `${selectedLot.pesoProm}kg` : "—",
                    ],
                    [
                      "Peso Total:",
                      selectedLot.pesoTotal
                        ? `${selectedLot.pesoTotal}kg`
                        : "—",
                    ],
                    ["Ubicación:", selectedLot.ubicacion || "—"],
                    [
                      "Valor Kg:",
                      selectedLot.valorKg
                        ? formatCOP(selectedLot.valorKg)
                        : "—",
                    ],
                    [
                      "Valor x Animal:",
                      selectedLot.valorPorAnimal
                        ? formatCOP(selectedLot.valorPorAnimal)
                        : "—",
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={String(label)}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        style={{
                          color: "rgba(255,255,255,0.65)",
                          fontSize: 14,
                        }}
                      >
                        {label}
                      </span>
                      <span
                        style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}
                      >
                        {value}
                      </span>
                    </div>
                  ))}
                </div>

                {selectedLot.status === "ACTIVO" && (
                  <div
                    style={{
                      marginTop: 12,
                      borderRadius: 12,
                      backgroundColor: "rgba(12,187,29,0.15)",
                      border: "1.5px solid #0cbb1d",
                      padding: "10px 14px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          color: "rgba(255,255,255,0.8)",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        💰 Oferta actual
                      </span>
                      <span
                        style={{
                          color: "#ffffff",
                          fontWeight: 900,
                          fontSize: 22,
                          letterSpacing: -0.5,
                        }}
                      >
                        {selectedLot.currentBid
                          ? formatCOP(selectedLot.currentBid)
                          : selectedLot.precioMinimo
                            ? formatCOP(selectedLot.precioMinimo)
                            : "Sin ofertas"}
                      </span>
                    </div>
                    {(selectedLot.bidCount ?? 0) > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: 3,
                        }}
                      >
                        <span
                          style={{
                            color: "rgba(255,255,255,0.6)",
                            fontSize: 11,
                          }}
                        >
                          {selectedLot.bidCount}{" "}
                          {selectedLot.bidCount === 1 ? "oferta" : "ofertas"}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div
                  style={{
                    marginTop: 16,
                    display: "flex",
                    gap: 8,
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      padding: "4px 14px",
                      borderRadius: 20,
                      fontSize: 17,
                      fontWeight: 700,
                      backgroundColor: statusColor[selectedLot.status] + "30",
                      color: statusColor[selectedLot.status],
                    }}
                  >
                    {statusLabel[selectedLot.status]}
                  </span>
                  {selectedLot.status === "PENDIENTE" && (
                    <button
                      onClick={() => handleActivar(selectedLot)}
                      disabled={actionLoading}
                      style={miniBtn("#0cbb1d")}
                    >
                      ▶ Activar
                    </button>
                  )}
                  <button
                    onClick={() => setShowParticipants(true)}
                    style={miniBtn("#0cbb1d")}
                  >
                    👥 Participantes (
                    {participants.filter((p) => p.aprobado).length}/
                    {participants.length})
                  </button>
                </div>

                {selectedLot.status === "CERRADO" &&
                  selectedLot.paletaGanadora && (
                    <div
                      style={{
                        marginTop: 12,
                        textAlign: "center",
                        padding: "8px 12px",
                        backgroundColor: "#ffffff",
                        borderRadius: 10,
                      }}
                    >
                      <div style={{ color: "#B3B3B3", fontSize: 20 }}>
                        🏆 Ganador
                      </div>
                      <div
                        style={{
                          color: "#0cbb1d",
                          fontWeight: 800,
                          fontSize: 18,
                        }}
                      >
                        Paleta #
                        {String(selectedLot.paletaGanadora).padStart(3, "0")}
                      </div>
                      {selectedLot.montoGanador != null && (
                        <div
                          style={{
                            color: "#000000",
                            fontWeight: 700,
                            fontSize: 17,
                          }}
                        >
                          {formatCOP(selectedLot.montoGanador)}
                        </div>
                      )}
                    </div>
                  )}
              </div>
            )}
          </div>

          {/* ── COLUMNA DERECHA: Ofertas ── */}
          <div style={{ flex: "0 0 40%", maxWidth: "40%", minWidth: 280 }}>
            <div
              style={{
                fontWeight: 800,
                fontSize: 20,
                color: "#1a3a2a",
                marginBottom: 14,
              }}
            >
              Ofertas realizadas
              {isSelectedActive && (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#0cbb1d",
                    marginLeft: 8,
                  }}
                >
                  ● En vivo
                </span>
              )}
            </div>

            {isSelectedActive && bids.length > 0 && (
              <div
                style={{
                  padding: "8px 14px",
                  borderRadius: 10,
                  marginBottom: 12,
                  backgroundColor: "#fff8e1",
                  border: "1px solid #ffc107",
                  fontSize: 13,
                  color: "#856404",
                  fontWeight: 600,
                }}
              >
                💡 Haz click en una oferta para cerrar la subasta con ese
                ganador
              </div>
            )}

            <div
              style={{
                maxHeight: "calc(100vh - 280px)",
                overflowY: "auto",
                paddingRight: 4,
              }}
            >
              {bidsLoading ? (
                <div
                  style={{ textAlign: "center", padding: 40, color: "#888" }}
                >
                  Cargando ofertas...
                </div>
              ) : bids.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: 40,
                    color: "#aaa",
                    fontSize: 14,
                  }}
                >
                  Aún no hay ofertas para este lote.
                </div>
              ) : (
                bids.map((bid, idx) => (
                  <BidCard
                    key={bid.bidId}
                    bid={bid}
                    isFirst={idx === 0}
                    onClick={() => handleBidClick(bid)}
                    disabled={!isSelectedActive}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL VIDEO EXPANDIDO ── */}
      {videoExpanded && (
        <div
          onClick={() => setVideoExpanded(false)}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            cursor: "zoom-out",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "85vw",
              maxWidth: 1100,
              aspectRatio: "16/9",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <LotVideoPlayer
              lot={activeLot}
              controls
              style={{ borderRadius: 12, width: "100%", height: "100%" }}
            />
            <button
              onClick={() => setVideoExpanded(false)}
              style={{
                position: "absolute",
                top: -16,
                right: -16,
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "none",
                backgroundColor: "#fff",
                fontSize: 18,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ── MODAL CERRAR SUBASTA ── */}
      {closingBid && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(2px)",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 24,
              padding: "32px 28px",
              width: 360,
              maxWidth: "92vw",
              boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
              textAlign: "center",
            }}
          >
            <h2
              style={{
                margin: "0 0 20px",
                fontWeight: 800,
                fontSize: 22,
                color: "#1a3a2a",
                lineHeight: 1.3,
              }}
            >
              ¿Desea Cerrar
              <br />
              esta Subasta?
            </h2>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
                marginBottom: 24,
              }}
            >
              <PaletaBadge number={closingBid.palletNumber} size={72} />
              <div style={{ textAlign: "left" }}>
                <div
                  style={{ fontSize: 26, fontWeight: 900, color: "#1a3a2a" }}
                >
                  {formatCOP(closingBid.amount)}
                </div>
                <div style={{ fontSize: 13, color: "#888" }}>
                  {formatDateEs(closingBid.createdAt)}
                </div>
                {closingBid.userName && closingBid.userName !== "—" && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#555",
                      marginTop: 2,
                      fontWeight: 600,
                    }}
                  >
                    {closingBid.userName}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => setClosingBid(null)}
                style={{
                  flex: 1,
                  height: 52,
                  borderRadius: 14,
                  border: "1px solid #dee2e6",
                  backgroundColor: "rgba(0,0,0,0.05)",
                  fontWeight: 700,
                  fontSize: 17,
                  cursor: "pointer",
                  color: "#333",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmClose}
                disabled={actionLoading}
                style={{
                  flex: 1,
                  height: 52,
                  borderRadius: 14,
                  border: "none",
                  backgroundColor: "#006b4c",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 17,
                  cursor: actionLoading ? "not-allowed" : "pointer",
                  opacity: actionLoading ? 0.7 : 1,
                }}
              >
                {actionLoading ? "..." : "Aceptar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL PARTICIPANTES ── */}
      {showParticipants && selectedLot && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 20,
              padding: 28,
              width: 520,
              maxWidth: "94vw",
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 18,
              }}
            >
              <h3 style={{ margin: 0, fontWeight: 800, color: "#1a3a2a" }}>
                👥 Participantes — Lote {selectedLot.nroLote}
              </h3>
              <button
                onClick={() => setShowParticipants(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 22,
                  cursor: "pointer",
                  color: "#888",
                }}
              >
                ✕
              </button>
            </div>

            <div
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                marginBottom: 14,
                backgroundColor: "#e8f5e9",
                border: "1px solid #0cbb1d",
                fontSize: 13,
                color: "#006b4c",
              }}
            >
              <strong>Para aprobar:</strong> el usuario debe enviar el
              comprobante de pago por WhatsApp (
              <a
                href="https://wa.me/573053841378"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#006b4c" }}
              >
                3053841378
              </a>
              ). Solo los usuarios aprobados pueden pujar.
            </div>

            {participantsLoading ? (
              <div style={{ textAlign: "center", padding: 24, color: "#888" }}>
                Cargando...
              </div>
            ) : participants.length === 0 ? (
              <div style={{ textAlign: "center", padding: 24, color: "#aaa" }}>
                Sin participantes registrados.
              </div>
            ) : (
              participants.map((p) => (
                <div
                  key={p.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 14px",
                    borderRadius: 12,
                    marginBottom: 8,
                    backgroundColor: p.aprobado ? "#f0fff4" : "#fff8f0",
                    border: `1px solid ${p.aprobado ? "#c3e6cb" : "#fce8d0"}`,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      backgroundColor: p.aprobado ? "#0cbb1d20" : "#f9811520",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: 12,
                      color: p.aprobado ? "#0cbb1d" : "#f98115",
                      flexShrink: 0,
                    }}
                  >
                    {p.numeroPaleta
                      ? String(p.numeroPaleta).padStart(3, "0")
                      : "—"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#1a3a2a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.userName || "—"}
                    </div>
                    <div style={{ fontSize: 12, color: "#888" }}>
                      {p.userEmail || p.userPhone || "—"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    {p.aprobado ? (
                      <>
                        <span
                          style={{
                            padding: "3px 10px",
                            borderRadius: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            backgroundColor: "#d4edda",
                            color: "#155724",
                          }}
                        >
                          ✓ Aprobado
                        </span>
                        <button
                          onClick={() => handleRevoke(p.userId)}
                          style={{ ...miniBtn("#D60D4C"), fontSize: 11 }}
                        >
                          Revocar
                        </button>
                      </>
                    ) : (
                      <>
                        <span
                          style={{
                            padding: "3px 10px",
                            borderRadius: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            backgroundColor: "#fff3cd",
                            color: "#856404",
                          }}
                        >
                          Pendiente
                        </span>
                        <button
                          onClick={() => handleApprove(p.userId)}
                          style={{ ...miniBtn("#0cbb1d"), fontSize: 11 }}
                        >
                          Aprobar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── MODAL REGISTRAR LOTE ── */}
      {showForm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 20,
              padding: 28,
              width: 560,
              maxWidth: "94vw",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
              }}
            >
              <h3 style={{ margin: 0, fontWeight: 800, color: "#1a3a2a" }}>
                + Registrar Nuevo Lote
              </h3>
              <button
                onClick={() => setShowForm(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 22,
                  cursor: "pointer",
                  color: "#888",
                }}
              >
                ✕
              </button>
            </div>

            <form
              onSubmit={handleSubmitLote}
              style={{ display: "flex", flexDirection: "column", gap: 14 }}
            >
              <div style={fieldRow}>
                <label style={labelStyle}>
                  Video del lote (archivo MP4/MOV)
                </label>
                <input
                  ref={videoRef}
                  type="file"
                  accept="video/*"
                  onChange={(e) => setVideoFile(e.target.files?.[0] ?? null)}
                  style={inputStyle}
                />
                <small style={{ color: "#888", marginTop: 4 }}>
                  MP4/MOV — máximo 100 MB. Se sube a S3 y reproduce en loop en
                  la app.
                </small>
                {videoFile && (
                  <small
                    style={{
                      color:
                        videoFile.size > 100 * 1024 * 1024
                          ? "#dc3545"
                          : "#0cbb1d",
                      marginTop: 4,
                      fontWeight: 700,
                    }}
                  >
                    {videoFile.size > 100 * 1024 * 1024
                      ? `⚠️ ${(videoFile.size / 1024 / 1024).toFixed(1)} MB — supera el límite de 100 MB`
                      : `✓ ${videoFile.name} — ${(videoFile.size / 1024 / 1024).toFixed(1)} MB`}
                  </small>
                )}
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <div style={fieldRow}>
                  <label style={labelStyle}>Cant. animales *</label>
                  <input
                    type="number"
                    min={1}
                    required
                    value={form.cantAnimales ?? 1}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, cantAnimales: +e.target.value }))
                    }
                    style={inputStyle}
                  />
                </div>
                <div style={fieldRow}>
                  <label style={labelStyle}>Tipo de ganado</label>
                  <input
                    value={form.tipoGanado ?? ""}
                    placeholder="ML, Brahman, Gyr..."
                    onChange={(e) =>
                      setForm((f) => ({ ...f, tipoGanado: e.target.value }))
                    }
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <div style={fieldRow}>
                  <label style={labelStyle}>Peso prom (kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.pesoProm ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        pesoProm: e.target.value ? +e.target.value : undefined,
                      }))
                    }
                    style={inputStyle}
                  />
                </div>
                <div style={fieldRow}>
                  <label style={labelStyle}>Peso total (kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.pesoTotal ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        pesoTotal: e.target.value ? +e.target.value : undefined,
                      }))
                    }
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <div style={fieldRow}>
                  <label style={labelStyle}>Valor / Kg ($)</label>
                  <input
                    type="number"
                    step="1"
                    value={form.valorKg ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        valorKg: e.target.value ? +e.target.value : undefined,
                      }))
                    }
                    style={inputStyle}
                  />
                </div>
                <div style={fieldRow}>
                  <label style={labelStyle}>Precio mínimo ($)</label>
                  <input
                    type="number"
                    step="1"
                    value={form.precioMinimo ?? 0}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, precioMinimo: +e.target.value }))
                    }
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={fieldRow}>
                <label style={labelStyle}>Ubicación / Referencia</label>
                <input
                  value={form.ubicacion ?? ""}
                  placeholder="Vereda El Roble, Km 3 vía principal..."
                  onChange={(e) =>
                    setForm((f) => ({ ...f, ubicacion: e.target.value }))
                  }
                  style={inputStyle}
                />
              </div>

              <div style={fieldRow}>
                <label style={labelStyle}>Observaciones</label>
                <textarea
                  rows={3}
                  value={form.observaciones ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, observaciones: e.target.value }))
                  }
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>

              {uploadProgress !== null && (
                <div style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 12,
                      color: "#555",
                      marginBottom: 4,
                      fontWeight: 600,
                    }}
                  >
                    <span>📤 Subiendo video...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 99,
                      backgroundColor: "#e9ecef",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        borderRadius: 99,
                        backgroundColor:
                          uploadProgress === 100 ? "#0cbb1d" : "#006b4c",
                        width: `${uploadProgress}%`,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                  {uploadProgress === 100 && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "#0cbb1d",
                        marginTop: 4,
                        fontWeight: 700,
                      }}
                    >
                      ✅ Video subido — procesando registro...
                    </p>
                  )}
                </div>
              )}

              <div
                style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}
              >
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  disabled={actionLoading}
                  style={{
                    ...cancelBtnStyle,
                    opacity: actionLoading ? 0.5 : 1,
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  style={btnStyle("#0cbb1d")}
                >
                  {uploadProgress !== null && uploadProgress < 100
                    ? `Subiendo video ${uploadProgress}%...`
                    : actionLoading
                      ? "Registrando..."
                      : "Registrar Lote"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ESTILOS ──────────────────────────────────────────────────────────────────

const btnStyle = (bg: string): React.CSSProperties => ({
  padding: "8px 18px",
  borderRadius: 10,
  border: "none",
  background: bg,
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
  fontSize: 14,
});
const miniBtn = (bg: string): React.CSSProperties => ({
  padding: "4px 10px",
  borderRadius: 8,
  border: "none",
  background: bg,
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
  fontSize: 12,
});
const cancelBtnStyle: React.CSSProperties = {
  padding: "8px 18px",
  borderRadius: 10,
  border: "1px solid #dee2e6",
  background: "#f8f9fa",
  color: "#333",
  fontWeight: 700,
  cursor: "pointer",
  fontSize: 14,
};
const alertStyle = (type: "success" | "error"): React.CSSProperties => ({
  padding: "10px 16px",
  borderRadius: 10,
  marginBottom: 14,
  fontWeight: 700,
  background: type === "success" ? "#d4edda" : "#f8d7da",
  color: type === "success" ? "#155724" : "#721c24",
  border: `1px solid ${type === "success" ? "#c3e6cb" : "#f5c6cb"}`,
});
const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  borderRadius: 10,
  border: "1px solid #dee2e6",
  fontSize: 14,
  boxSizing: "border-box",
};
const labelStyle: React.CSSProperties = {
  fontWeight: 700,
  fontSize: 13,
  color: "#333",
  marginBottom: 5,
  display: "block",
};
const fieldRow: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
};
